import type { IconProps } from "../Icon.props";
import createWebIcon from "../createWebIcon";
import validateKeys from "../validateKeys";

// =============================================================================
// Direction Icons (viewBox 36x16 and 16x32)
// =============================================================================

export function DirectionRow(props: IconProps): JSX.Element {
  const Icon = createWebIcon({
    ...props,
    name: "DirectionRow",
    viewBox: "0 0 36 16",
    path: (
      <>
        <path
          d="M11 5 L13 8 L11 11"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="icon-outline"
          opacity={0.4}
        />
        <path
          d="M23 5 L25 8 L23 11"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="icon-outline"
          opacity={0.4}
        />
        <rect
          x={2}
          y={5.5}
          width={5}
          height={5}
          rx={1}
          className="icon-primary"
        />
        <rect
          x={15}
          y={4.5}
          width={6}
          height={7}
          rx={1}
          className="icon-primary"
        />
        <rect
          x={27}
          y={3}
          width={7}
          height={10}
          rx={1}
          className="icon-primary"
        />
      </>
    ),
  });
  return <Icon {...validateKeys(props)} />;
}

export function DirectionColumn(props: IconProps): JSX.Element {
  const Icon = createWebIcon({
    ...props,
    name: "DirectionColumn",
    viewBox: "0 0 16 32",
    path: (
      <>
        <path
          d="M5 9 L8 11 L11 9"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="icon-outline"
          opacity={0.4}
        />
        <path
          d="M5 20 L8 22 L11 20"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="icon-outline"
          opacity={0.4}
        />
        <rect
          x={5.5}
          y={2}
          width={5}
          height={4}
          rx={1}
          className="icon-primary"
        />
        <rect
          x={4.5}
          y={13}
          width={7}
          height={5}
          rx={1}
          className="icon-primary"
        />
        <rect
          x={3}
          y={24}
          width={10}
          height={6}
          rx={1}
          className="icon-primary"
        />
      </>
    ),
  });
  return <Icon {...validateKeys(props)} />;
}

// =============================================================================
// Alignment Row Icons (viewBox 32x16) - items aligned within a horizontal container
// =============================================================================

export function AlignRowStart(props: IconProps): JSX.Element {
  const Icon = createWebIcon({
    ...props,
    name: "AlignRowStart",
    viewBox: "0 0 32 16",
    path: (
      <>
        <rect
          x={0.5}
          y={0.5}
          width={31}
          height={15}
          rx={1}
          stroke="currentColor"
          strokeWidth={1}
          className="icon-outline"
          opacity={0.4}
        />
        {[4, 11, 18, 25].map((x) => (
          <rect
            key={x}
            x={x}
            y={2}
            width={3}
            height={4}
            rx={0.5}
            className="icon-primary"
          />
        ))}
      </>
    ),
  });
  return <Icon {...validateKeys(props)} />;
}

export function AlignRowCenter(props: IconProps): JSX.Element {
  const Icon = createWebIcon({
    ...props,
    name: "AlignRowCenter",
    viewBox: "0 0 32 16",
    path: (
      <>
        <rect
          x={0.5}
          y={0.5}
          width={31}
          height={15}
          rx={1}
          stroke="currentColor"
          strokeWidth={1}
          className="icon-outline"
          opacity={0.4}
        />
        {[4, 11, 18, 25].map((x) => (
          <rect
            key={x}
            x={x}
            y={6}
            width={3}
            height={4}
            rx={0.5}
            className="icon-primary"
          />
        ))}
      </>
    ),
  });
  return <Icon {...validateKeys(props)} />;
}

export function AlignRowEnd(props: IconProps): JSX.Element {
  const Icon = createWebIcon({
    ...props,
    name: "AlignRowEnd",
    viewBox: "0 0 32 16",
    path: (
      <>
        <rect
          x={0.5}
          y={0.5}
          width={31}
          height={15}
          rx={1}
          stroke="currentColor"
          strokeWidth={1}
          className="icon-outline"
          opacity={0.4}
        />
        {[4, 11, 18, 25].map((x) => (
          <rect
            key={x}
            x={x}
            y={10}
            width={3}
            height={4}
            rx={0.5}
            className="icon-primary"
          />
        ))}
      </>
    ),
  });
  return <Icon {...validateKeys(props)} />;
}

export function AlignRowStretch(props: IconProps): JSX.Element {
  const Icon = createWebIcon({
    ...props,
    name: "AlignRowStretch",
    viewBox: "0 0 32 16",
    path: (
      <>
        <rect
          x={0.5}
          y={0.5}
          width={31}
          height={15}
          rx={1}
          stroke="currentColor"
          strokeWidth={1}
          className="icon-outline"
          opacity={0.4}
        />
        {[4, 11, 18, 25].map((x) => (
          <rect
            key={x}
            x={x}
            y={2}
            width={3}
            height={12}
            rx={0.5}
            className="icon-primary"
          />
        ))}
      </>
    ),
  });
  return <Icon {...validateKeys(props)} />;
}

// =============================================================================
// Alignment Column Icons (viewBox 32x16) - items aligned within a vertical container
// =============================================================================

export function AlignColStart(props: IconProps): JSX.Element {
  const Icon = createWebIcon({
    ...props,
    name: "AlignColStart",
    viewBox: "0 0 32 16",
    path: (
      <>
        <rect
          x={0.5}
          y={0.5}
          width={31}
          height={15}
          rx={1}
          stroke="currentColor"
          strokeWidth={1}
          className="icon-outline"
          opacity={0.4}
        />
        {[2, 5, 8, 11].map((y) => (
          <rect
            key={y}
            x={2}
            y={y}
            width={4}
            height={2.5}
            rx={0.5}
            className="icon-primary"
          />
        ))}
      </>
    ),
  });
  return <Icon {...validateKeys(props)} />;
}

export function AlignColCenter(props: IconProps): JSX.Element {
  const Icon = createWebIcon({
    ...props,
    name: "AlignColCenter",
    viewBox: "0 0 32 16",
    path: (
      <>
        <rect
          x={0.5}
          y={0.5}
          width={31}
          height={15}
          rx={1}
          stroke="currentColor"
          strokeWidth={1}
          className="icon-outline"
          opacity={0.4}
        />
        {[2, 5, 8, 11].map((y) => (
          <rect
            key={y}
            x={14}
            y={y}
            width={4}
            height={2.5}
            rx={0.5}
            className="icon-primary"
          />
        ))}
      </>
    ),
  });
  return <Icon {...validateKeys(props)} />;
}

export function AlignColEnd(props: IconProps): JSX.Element {
  const Icon = createWebIcon({
    ...props,
    name: "AlignColEnd",
    viewBox: "0 0 32 16",
    path: (
      <>
        <rect
          x={0.5}
          y={0.5}
          width={31}
          height={15}
          rx={1}
          stroke="currentColor"
          strokeWidth={1}
          className="icon-outline"
          opacity={0.4}
        />
        {[2, 5, 8, 11].map((y) => (
          <rect
            key={y}
            x={26}
            y={y}
            width={4}
            height={2.5}
            rx={0.5}
            className="icon-primary"
          />
        ))}
      </>
    ),
  });
  return <Icon {...validateKeys(props)} />;
}

export function AlignColStretch(props: IconProps): JSX.Element {
  const Icon = createWebIcon({
    ...props,
    name: "AlignColStretch",
    viewBox: "0 0 32 16",
    path: (
      <>
        <rect
          x={0.5}
          y={0.5}
          width={31}
          height={15}
          rx={1}
          stroke="currentColor"
          strokeWidth={1}
          className="icon-outline"
          opacity={0.4}
        />
        {[2, 5, 8, 11].map((y) => (
          <rect
            key={y}
            x={2}
            y={y}
            width={28}
            height={2.5}
            rx={0.5}
            className="icon-primary"
          />
        ))}
      </>
    ),
  });
  return <Icon {...validateKeys(props)} />;
}

// =============================================================================
// Distribution Icons (viewBox 32x20) - justify-content visualization
// =============================================================================

function DistributionEdgeLines(): JSX.Element {
  return (
    <>
      <line
        x1={2}
        y1={3}
        x2={2}
        y2={17}
        stroke="currentColor"
        strokeWidth={1.5}
        className="icon-outline"
        opacity={0.3}
      />
      <line
        x1={30}
        y1={3}
        x2={30}
        y2={17}
        stroke="currentColor"
        strokeWidth={1.5}
        className="icon-outline"
        opacity={0.3}
      />
    </>
  );
}

export function DistributeStart(props: IconProps): JSX.Element {
  const Icon = createWebIcon({
    ...props,
    name: "DistributeStart",
    viewBox: "0 0 32 20",
    path: (
      <>
        <DistributionEdgeLines />
        <rect
          x={3}
          y={6}
          width={5}
          height={8}
          rx={1}
          className="icon-primary"
        />
        <rect
          x={10}
          y={6}
          width={5}
          height={8}
          rx={1}
          className="icon-primary"
        />
      </>
    ),
  });
  return <Icon {...validateKeys(props)} />;
}

export function DistributeCenter(props: IconProps): JSX.Element {
  const Icon = createWebIcon({
    ...props,
    name: "DistributeCenter",
    viewBox: "0 0 32 20",
    path: (
      <>
        <DistributionEdgeLines />
        <rect
          x={10}
          y={6}
          width={5}
          height={8}
          rx={1}
          className="icon-primary"
        />
        <rect
          x={17}
          y={6}
          width={5}
          height={8}
          rx={1}
          className="icon-primary"
        />
      </>
    ),
  });
  return <Icon {...validateKeys(props)} />;
}

export function DistributeEnd(props: IconProps): JSX.Element {
  const Icon = createWebIcon({
    ...props,
    name: "DistributeEnd",
    viewBox: "0 0 32 20",
    path: (
      <>
        <DistributionEdgeLines />
        <rect
          x={17}
          y={6}
          width={5}
          height={8}
          rx={1}
          className="icon-primary"
        />
        <rect
          x={24}
          y={6}
          width={5}
          height={8}
          rx={1}
          className="icon-primary"
        />
      </>
    ),
  });
  return <Icon {...validateKeys(props)} />;
}

export function DistributeBetween(props: IconProps): JSX.Element {
  const Icon = createWebIcon({
    ...props,
    name: "DistributeBetween",
    viewBox: "0 0 32 20",
    path: (
      <>
        <DistributionEdgeLines />
        <rect
          x={3}
          y={6}
          width={5}
          height={8}
          rx={1}
          className="icon-primary"
        />
        <rect
          x={24}
          y={6}
          width={5}
          height={8}
          rx={1}
          className="icon-primary"
        />
      </>
    ),
  });
  return <Icon {...validateKeys(props)} />;
}

export function DistributeAround(props: IconProps): JSX.Element {
  const Icon = createWebIcon({
    ...props,
    name: "DistributeAround",
    viewBox: "0 0 32 20",
    path: (
      <>
        <DistributionEdgeLines />
        <rect
          x={6}
          y={6}
          width={5}
          height={8}
          rx={1}
          className="icon-primary"
        />
        <rect
          x={21}
          y={6}
          width={5}
          height={8}
          rx={1}
          className="icon-primary"
        />
      </>
    ),
  });
  return <Icon {...validateKeys(props)} />;
}

export function DistributeEvenly(props: IconProps): JSX.Element {
  const Icon = createWebIcon({
    ...props,
    name: "DistributeEvenly",
    viewBox: "0 0 32 20",
    path: (
      <>
        <DistributionEdgeLines />
        <rect
          x={7}
          y={6}
          width={5}
          height={8}
          rx={1}
          className="icon-primary"
        />
        <rect
          x={20}
          y={6}
          width={5}
          height={8}
          rx={1}
          className="icon-primary"
        />
      </>
    ),
  });
  return <Icon {...validateKeys(props)} />;
}

// =============================================================================
// Wrap Icons (viewBox 24x16)
// =============================================================================

export function FlexWrapNone(props: IconProps): JSX.Element {
  const Icon = createWebIcon({
    ...props,
    name: "FlexWrapNone",
    viewBox: "0 0 24 16",
    path: (
      <>
        <rect
          x={2}
          y={6}
          width={4}
          height={4}
          rx={0.5}
          className="icon-primary"
        />
        <rect
          x={8}
          y={6}
          width={4}
          height={4}
          rx={0.5}
          className="icon-primary"
        />
        <rect
          x={14}
          y={6}
          width={4}
          height={4}
          rx={0.5}
          className="icon-primary"
        />
        <rect
          x={20}
          y={6}
          width={4}
          height={4}
          rx={0.5}
          className="icon-primary"
          opacity={0.3}
        />
      </>
    ),
  });
  return <Icon {...validateKeys(props)} />;
}

export function FlexWrapDown(props: IconProps): JSX.Element {
  const Icon = createWebIcon({
    ...props,
    name: "FlexWrapDown",
    viewBox: "0 0 24 16",
    path: (
      <>
        <rect
          x={2}
          y={2}
          width={4}
          height={4}
          rx={0.5}
          className="icon-primary"
        />
        <rect
          x={8}
          y={2}
          width={4}
          height={4}
          rx={0.5}
          className="icon-primary"
        />
        <rect
          x={14}
          y={2}
          width={4}
          height={4}
          rx={0.5}
          className="icon-primary"
        />
        <rect
          x={2}
          y={8}
          width={4}
          height={4}
          rx={0.5}
          className="icon-primary"
        />
      </>
    ),
  });
  return <Icon {...validateKeys(props)} />;
}

export function FlexWrapUp(props: IconProps): JSX.Element {
  const Icon = createWebIcon({
    ...props,
    name: "FlexWrapUp",
    viewBox: "0 0 24 16",
    path: (
      <>
        <rect
          x={2}
          y={10}
          width={4}
          height={4}
          rx={0.5}
          className="icon-primary"
        />
        <rect
          x={8}
          y={10}
          width={4}
          height={4}
          rx={0.5}
          className="icon-primary"
        />
        <rect
          x={14}
          y={10}
          width={4}
          height={4}
          rx={0.5}
          className="icon-primary"
        />
        <rect
          x={2}
          y={4}
          width={4}
          height={4}
          rx={0.5}
          className="icon-primary"
        />
      </>
    ),
  });
  return <Icon {...validateKeys(props)} />;
}
